import format from "string-format";
import { JsonElementType, JsonObject, JsonProperty } from "ta-json";

import type { MapCultureTo, Nullable } from "../../base-types";
import ErrorMessages from "../../error-messages";
import { InvalidOperationError } from "../../errors/invalid-operation-error";
import Guard from "../../guard";
import { IMemberDefinition } from "./member-definition";
import { IMemberGroup, MemberGroup } from "./member-group";
import { IPropertyDefinition, PropertyDefinition } from "./property-definition";
import { IRelationDefinition, RelationDefinition } from "./relation-definition";
import { RelationRole } from "./relation-role";
import IResource from "./resource";

/**
 * Represents a data schema / domain model for an entity.
 */
export interface IEntityDefinition extends IResource {
    /**
     * Gets the id of this definition.
     * This is an automatically assigned unique and strictly positive number identifying the entity definition.
     * Value is 0, null or undefined when the definition has not been persisted.
     */
    readonly id?: number;

    /**
     * Gets a value indicating whether the entity definition is new.
     */
    readonly isNew: boolean;

    /**
     * Gets the unique and language agnostic name of the entity definition.
     * It should be handled as case sensitive.
     */
    name: string;

    /**
     * Gets the language agnostic display format for entities of this definition.
     */
    displayTemplate: string;

    /**
     * Gets a value indicating whether entities of this entity definition are taxonomy items.
     * They can then be used as a search facet.
     */
    isTaxonomyItemDefinition: boolean;

    /**
     * Gets a value indicating whether entities of this entity definition can be part of the so called paths, which are technically UI breadcrumbs.
     * Example use cases are taxonomy entity definitions or M Project block entity definitions.
     */
    isPathEnabledDefinition: boolean;

    /**
     * Gets a value indicating whether the items of this definition can be sorted manually (UI related).
     */
    isManualSortingAllowed: boolean;

    /**
     * Gets a value indicating whether the entity definition is owned by the system and cannot be modified or deleted by the regular users.
     */
    isSystemOwned: boolean;

    /**
     * Gets a value indicating whether the entity definition is a light entity definition, meaning that it is not persisted in the elastic search index.
     */
    isLight: boolean;

    /**
     * Gets the collection of {@link IMemberGroup}s.
     */
    memberGroups: Array<IMemberGroup>;

    /**
     * Gets a collection of the culture specific definition labels.
     */
    labels: MapCultureTo<string>;

    /**
     * The permissions the current user has for this entity definition.
     */
    permissions?: Array<string>;

    /**
     * Usage type of definition. Can be 'Normal', 'Light', 'External'
     */
    usageType: string;

    /**
     * Get the definition of the specified property.
     * @param name - The name of the property
     * @returns The property definition or null.
     */
    getPropertyDefinition(name: string): Nullable<IPropertyDefinition>;

    /**
     * Generic version to get the definition of the specified property.
     * @param name - The name of the property
     * @returns The property definition or null.
     */
    getPropertyDefinition<T extends IPropertyDefinition>(name: string): Nullable<T>;

    /**
     * Gets all property definitions.
     * @returns A list of all property definitions.
     */
    getPropertyDefinitions(): Array<IPropertyDefinition>;

    /**
     * Get the definition of the specified relation.
     * @param name - Name of the relation
     * @param role - Relation role
     * @returns The relation definition or null.
     * @throws An {@link InvalidOperationError} when no role is given and the relation is a self-relation.
     */
    getRelationDefinition(name: string, role?: RelationRole): Nullable<IRelationDefinition>;

    /**
     * Get all relation definitions.
     * @returns A list of all relations definitions.
     */
    getRelationDefinitions(): Array<IRelationDefinition>;
}

@JsonObject()
export class EntityDefinition implements IEntityDefinition {
    @JsonProperty("id")
    readonly id?: number;

    get isNew(): boolean {
        return !this.id;
    }

    @JsonProperty("name")
    name: string;

    @JsonProperty("created_by")
    readonly createdBy?: number;

    @JsonProperty("created_on")
    readonly createdOn?: Date;

    @JsonProperty("modified_by")
    readonly modifiedBy?: number;

    @JsonProperty("modified_on")
    readonly modifiedOn?: Date;

    @JsonProperty("display_template")
    displayTemplate: string;

    @JsonProperty("is_taxonomy_item_definition")
    isTaxonomyItemDefinition: boolean;

    @JsonProperty("is_path_enabled_definition")
    isPathEnabledDefinition: boolean;

    @JsonProperty("is_manual_sorting_allowed")
    isManualSortingAllowed: boolean;

    @JsonProperty("is_system_owned")
    isSystemOwned: boolean = false;

    @JsonProperty("is_light")
    isLight: boolean = false;

    @JsonProperty("member_groups")
    @JsonElementType(MemberGroup)
    memberGroups: Array<IMemberGroup>;

    @JsonProperty("labels")
    labels: MapCultureTo<string>;

    permissions?: Array<string>;

    @JsonProperty("usage_type")
    usageType: string;

    constructor(init?: Partial<EntityDefinition>) {
        Object.assign(this, init);
        this.memberGroups = this.memberGroups || [];
        this.labels = this.labels || {};
    }

    getPropertyDefinition<T extends IPropertyDefinition>(name: string): Nullable<T> {
        Guard.stringNotNullOrEmpty(name);

        return (this.getPropertyDefinitions().find(propertyDefinition => {
            return propertyDefinition.name.toLowerCase() === name.toLowerCase();
        }) || null) as T;
    }

    getPropertyDefinitions(): Array<IPropertyDefinition> {
        return this.getMemberDefinitions<IPropertyDefinition>(PropertyDefinition);
    }

    getRelationDefinition(name: string, role?: RelationRole): Nullable<IRelationDefinition> {
        Guard.stringNotNullOrEmpty(name);

        const relationDefinition = this.getRelationDefinitions().find(relationDefinition => {
            return (
                relationDefinition.name.toLowerCase() === name.toLowerCase() &&
                (role == null || relationDefinition.role === role)
            );
        });

        if (relationDefinition?.associatedEntityDefinitionName === this.name && role == null) {
            const errorMessage = format(ErrorMessages.NoRoleSpecifiedForSelfRelation, name);
            throw new InvalidOperationError(errorMessage);
        }

        return relationDefinition ?? null;
    }

    getRelationDefinitions(): Array<IRelationDefinition> {
        return this.getMemberDefinitions<IRelationDefinition>(RelationDefinition);
    }

    private getMemberDefinitions<T extends IMemberDefinition>(typeT: new (...params: Array<any>) => T): Array<T> {
        const members: Array<T> = [];

        for (const group of this.memberGroups) {
            for (const member of group.memberDefinitions) {
                if (member instanceof typeT) {
                    members.push(member);
                }
            }
        }

        return members;
    }
}
